import Store from 'store/store';
import { UNIVERSAL_STUDIO_SLUG, FOCUS_STUDIO_SLUG, LOCAL_MOVIE_TYPE, MOVIE_PLATFORM_TYPE } from 'utils/constants';
import { getMovieType, isPlaceholder } from 'utils/data';
import { getCurrentRegionSettings, isBaseRegion, isFocusStudio } from 'utils/studioConfig';

import MovieInfoEditor from 'components/editor/movies/movieInfo';
import MovieMediaEditor from 'components/editor/movies/movieMedia';
import MoviePageEditor from 'components/editor/movies/moviePage';
import MovieSidebar from 'components/editor/movies/movieSidebar';

import AboutEditor from 'components/editor/pages/about';

import UniversalLandingEditor from 'components/editor/pages/landing';
import UniversalLeadershipEditor from 'components/editor/pages/leadership';
import UniversalNewsEditor from 'components/editor/pages/news';

import FocusContactEditor from 'components/editor/pages/focus/contact';
import FocusLandingEditor from 'components/editor/pages/focus/landing';

const pageEditors = {
	about: AboutEditor,
	[UNIVERSAL_STUDIO_SLUG]: {
		landing: UniversalLandingEditor,
		news: UniversalNewsEditor,
		leadership: UniversalLeadershipEditor,
	},
	[FOCUS_STUDIO_SLUG]: {
		contact: FocusContactEditor,
		landing: FocusLandingEditor,
	},
};

export function getMovieData(id) {
	const movies = Store.get().data?.movies || {};
	const localeMovie = movies[id];
	if (localeMovie) return localeMovie;
	const baseMovie = Object.values(movies).find(movie => movie.base_movie_id === id);
	return baseMovie || null;
}

/** Data path of the item currently being edited, or null when not in an editor */
export function getCurrentDataPath() {
	// Not ideal, we should unify how the data path is computed across components
	const navPath = Store.get().path;
	if (navPath[0] === 'navigation') {
		return '/navigation';
	}
	if (navPath.length > 1 && (navPath[0] === 'pages' || navPath[0] === 'movies')) {
		const config = getEditorConfigFromURL(navPath);
		const pathStr = navPath.join('/');
		const section = config?.sections?.find(section => pathStr.startsWith(`${config.basePath}/${section.path}`));
		return section?.dataRoot || null;
	}
	return null;
}

// This should be somewhere else
export async function showCropper() {
	document.getElementById('cropper')?.showModal();
}

export async function hideCropper() {
	document.getElementById('cropper')?.close();
}

function getPagesEditorConfig(slug) {
	const { data, domains } = Store.get();
	const { studioSlug, regionCode } = domains || {};

	const page = Object.values(data?.pages || {}).find((page) => page.slug === slug);
	if (!page) return;

	const Editor = pageEditors[`${studioSlug}:${regionCode}`]?.[slug] || pageEditors[studioSlug]?.[slug] || pageEditors[slug];
	return {
		id: page.id,
		slug,
		basePath: `pages/${slug}`,
		title: page.name,
		sections: [
			{
				path: 'edit',
				name: 'Page editor',
				content: Editor,
				dataRoot: `/pages/${page.id}`,
			},
		],
	};
}

function getMoviesEditorConfig(movie) {
	if (!movie) return;

	const movieId = movie.id;
	const movieType = getMovieType(movie);
	const placeholder = isPlaceholder(movie);
	const isBase = isBaseRegion();
	const isFocus = isFocusStudio();
	const homeEntEnabled = getCurrentRegionSettings()?.homeEnt;

	const isLocalMoviePage = movieType === LOCAL_MOVIE_TYPE;

	const showMovieInfo = true;
	const showMovieMedia = isBase || isFocus || !placeholder;
	const showFocusPlaceholderPage = isFocus && placeholder;
	const showPages = !isBase && !placeholder;
	const showLandingPageSections = !isFocus;
	const showHomeEnt = homeEntEnabled && movieType === MOVIE_PLATFORM_TYPE;

	const movieInfoConfig = {
		path: 'info',
		name: 'Movie info',
		content: MovieInfoEditor,
		dataRoot: `/movies/${movieId}`,
	};

	const movieMediaConfig = {
		path: 'media',
		name: 'Media',
		content: MovieMediaEditor,
		dataRoot: `/movies/${movieId}`,
	};

	const focusPlaceholderPageConfig = {
		path: 'pages/home',
		name: 'Movie page',
		dataRoot: `/movies/${movieId}`,
		content: MoviePageEditor,
	};

	const landingPageConfig = {
		name: 'Landing',
		path: 'home',
		dataRoot: '/home',
		...(showLandingPageSections ? {
			pageSections: [
				{ id: 'synopsis', name: 'Synopsis' },
				{ id: 'gallery', name: 'Gallery' },
				{ id: 'videos', name: 'Videos' },
			]
		} : {})
	};

	const pagesConfig = {
		path: 'pages',
		name: 'Pages',
		dataRoot: `/movies/${movieId}`,
		content: MoviePageEditor,
		pages: [
			landingPageConfig,
			{ path: 'showtimes', name: 'Showtimes', dataRoot: '/showtimes', badges: [{ type: 'live', path: '/active' }] },
			showHomeEnt && { path: 'home-ent', name: 'HomeEnt', dataRoot: '/homeEnt', badges: [{ type: 'live', path: '/active' }] },
		].filter(Boolean),
	};


	return {
		id: movieId,
		placeholder,
		basePath: `movies/${movie?.base_movie_id || movieId}`,
		title: movie.content?.title || movie.title || movie?.base_movie?.content?.title || movie?.base_movie?.title,
		sidebar: MovieSidebar,
		sections: [
			showMovieInfo && movieInfoConfig,
			showMovieMedia && movieMediaConfig,
			!isLocalMoviePage && showPages && pagesConfig,
			showFocusPlaceholderPage && focusPlaceholderPageConfig,
		].filter(Boolean),
	};
}

export function getEditorConfigFromURL(path) {
	const type = path[0];

	if (type === 'pages') {
		const slug = path[1];
		return getPagesEditorConfig(slug);
	}

	if (type === 'movies') {
		const id = path[1];
		const movie = getMovieData(id);
		return getMoviesEditorConfig(movie);
	}
}
